import { expose } from 'comlink';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Logger } from '../../logger/logger';
import { LoggerConfig } from '../../logger/logger-config';
import { LogLevel } from '../../logger/enum/log-level';
import { ConsoleLogAppender } from '../../logger/appenders/console-log-appender';
import { WSEventType } from '../ws/enum/ws-event-type';
import {WsReconnectInfo} from '../ws/model/ws-reconnect-info';

const consoleAppender = new ConsoleLogAppender();
const loggerConfig = new LoggerConfig(LogLevel.INFO);
loggerConfig.addAppender(consoleAppender);

Logger.setConfig(loggerConfig);
/**
 *
 */
export class WSWorker {
  private logger: Logger = new Logger('SDK', 'WSWorker');
   wsSubject!: WebSocketSubject<any>;
  private wsReconnectAttempt = 0;
  private isConnectionOpen = false;


/**
 * This function used to make connection with websocket server
 * 
 * @param  websocketUrl  - websocket url
 * @param callback - callback method which will be executed when time interval is completed 
 * 
 * @example
 * 
 * ```
 * connect('ws://localhost:8089');
 * ```
 * 
 */
  connect(websocketUrl: string, callback: (event?: any) => void) {
    this.close();
    this.wsSubject = webSocket({
      url: websocketUrl,
      openObserver: {
        next: () => { callback({ type: WSEventType.OPEN }); this.isConnectionOpen = true; }
      },
      closeObserver: {
        next: () => { callback({ type: WSEventType.CLOSE }); }
      }
    });

    this.wsSubject.asObservable().subscribe(
      msg => { callback({ type: WSEventType.MESSAGE, message: msg  }); },
      err => {
        callback({ type: WSEventType.ERROR});
      },
      () => { callback({ type: WSEventType.CLOSE }); }
    );
  }

  /**
   * This method used reconnect websocket
   * @example
   * 
   * ```
   * attemptReconnect(data);
   * ```
   * @param data - which is object contain errReason and reasonToReconnect
   * @param callback - callback method which will be executed when time interval is completed 
   * 
   */
  attemptReconnect = (reconnectInfo: WsReconnectInfo, callback: (event?: any) => void) => {
      if (this.wsReconnectAttempt < reconnectInfo.retryOptions.maxRetryAttempts && !this.isConnectionOpen) {
        this.wsReconnectAttempt++;
        const message = `Websocket reconnect attempt ${this.wsReconnectAttempt} of ${reconnectInfo.retryOptions.maxRetryAttempts}...`;
        this.logger.info('attemptReconnect', message );
        const reconnectResponse = {
          maxAttempts: reconnectInfo.retryOptions.maxRetryAttempts,
          currentAttempt: this.wsReconnectAttempt
        }
        callback({ type: WSEventType.RECONNECT, message: reconnectResponse  });
        setTimeout(() => {
          this.connect(reconnectInfo.url, callback.bind(this));
        }, reconnectInfo.retryOptions.retryInterval);
      } else {
        this.logger.error('attemptReconnect', `Websocket reconnect failed`);
        callback({ type: WSEventType.RECONNECT_COMPLETE });
        this.isConnectionOpen = false;
      }
  }

  /**
   * This Method used to dispose the websocket connection 
   *@example
   * ```
   * close();
   * ```
   */
  close() {
    this.wsSubject?.complete();
  }

  /**
   * Method to send message to websocket server
   * 
   * @param msg - message
   * @example - 
   * 
   * ```
   * send('hi');
   * ```
   */
  send(msg: any) {
    if (this.wsSubject) {
      this.logger.info( 'send','Send Message to websocket server');
      this.wsSubject.next(msg);
    }
  }
}



expose(new WSWorker());